import { useCallback, useState } from 'react';

import {
  type ContextSelectionItem,
  type ContextSelectionType,
} from '@/components/chat/ContextSelectionContext';
import type { ToolItem } from '@/components/chat/chat-context-menu/hooks/useTools';
import { buildToolContextSelectionItem } from '@/components/chat/chat-context-menu/toolContextSelection';

type PendingToolPick = {
  toolItem: ToolItem;
  selectionType: ContextSelectionType;
};

interface UseToolVersionPickerFlowParams {
  onCommit: (item: ContextSelectionItem) => void;
}

export const useToolVersionPickerFlow = ({
  onCommit,
}: UseToolVersionPickerFlowParams) => {
  const [pending, setPending] = useState<PendingToolPick | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const requestToolSelection = useCallback(
    (toolItem: ToolItem, selectionType: ContextSelectionType) => {
      if (toolItem.versions.length === 0) {
        return;
      }

      if (toolItem.versions.length === 1) {
        const selection = buildToolContextSelectionItem(
          toolItem,
          [toolItem.versions[0].id],
          selectionType
        );

        onCommit(selection);

        return;
      }

      setPending({ toolItem, selectionType });
      setIsSheetOpen(true);
    },
    [onCommit]
  );

  const confirmVersionSelection = useCallback(
    (selectedIds: string[]) => {
      if (!pending) {
        return;
      }

      if (selectedIds.length === 0) {
        setIsSheetOpen(false);
        setPending(null);

        return;
      }

      const selection = buildToolContextSelectionItem(
        pending.toolItem,
        selectedIds,
        pending.selectionType
      );

      onCommit(selection);
      setIsSheetOpen(false);
      setPending(null);
    },
    [pending, onCommit]
  );

  const handleSheetOpenChange = useCallback((open: boolean) => {
    setIsSheetOpen(open);

    if (!open) {
      setPending(null);
    }
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
    setPending(null);
  }, []);

  return {
    pendingToolItem: pending?.toolItem ?? null,
    isSheetOpen,
    requestToolSelection,
    confirmVersionSelection,
    handleSheetOpenChange,
    closeSheet,
  };
};
